import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2, Building } from 'lucide-react';

import { AppShellFrame } from '@/shared/ui/AppShellFrame';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/shared/ui/components/sidebar';
import { SidebarAccountMenu } from '@/modules/auth/SidebarAccountMenu';
import { SelfShiftWidget } from '@/modules/workforce/SelfShiftWidget';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization, type Authorizer } from '@/modules/auth/useAuthorization';
import { useCanEnterAdmin } from '@/modules/admin/adminAccess';
import { useVenueContext } from './VenueContext';
import { VenueSwitcher } from './VenueSwitcher';
import { NAV_GROUP_LABEL_KEYS, NAV_GROUP_ORDER, NAV_ITEMS, type NavItem } from './venueNav';

/**
 * The Venue workspace shell wrapping `/loc/:venueId/...` (see routes.tsx). Distinct
 * from the franchise `AdminShell`: its sidebar shows the current Venue's name and only the
 * Venue-bound features, each gated at `{ kind: 'venue', venueId }`, plus a gated
 * "Franchise administration" cross-link into `/admin`.
 *
 * Chrome (floating collapsible sidebar + primary header band) comes from {@link AppShellFrame}.
 */
export function VenueShell() {
  const { t } = useTranslation('common');
  const { user, venues } = useAuth();
  const auth = useAuthorization();
  const canEnterAdmin = useCanEnterAdmin();
  const { venueId } = useVenueContext();
  const { pathname } = useLocation();

  const items = NAV_ITEMS.filter((item) => isVisible(item, auth, venueId));
  const ungroupedItems = items.filter((item) => !item.group);

  const venueName = venues.find((venue) => venue.id === venueId)?.name;

  const isActive = (to: string, end?: boolean) =>
    end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    if (item.externalPath) {
      return (
        <SidebarMenuItem key={item.path || 'home'}>
          <SidebarMenuButton asChild tooltip={t(item.labelKey)}>
            <a href={item.externalPath(venueId)} target="_blank" rel="noopener noreferrer">
              <Icon aria-hidden="true" />
              <span>{t(item.labelKey)}</span>
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
    const to = item.path ? `/loc/${venueId}/${item.path}` : `/loc/${venueId}`;
    return (
      <SidebarMenuItem key={item.path || 'home'}>
        <SidebarMenuButton asChild isActive={isActive(to, item.end)} tooltip={t(item.labelKey)}>
          <Link to={to}>
            <Icon aria-hidden="true" />
            <span>{t(item.labelKey)}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <AppShellFrame
      title={venueName ?? t('workspace.sectionHeader')}
      subtitle={venueName ? t('workspace.sectionHeader') : undefined}
      navLabel={t('workspace.sectionHeader')}
      sidebarHeader={
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="gap-2.5 [&>svg]:size-6" asChild>
              <Link to="/">
                <Building className="text-primary" />
                <span className="text-base font-semibold">{t('app.name')}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
            <VenueSwitcher />
          </SidebarMenuItem>
        </SidebarMenu>
      }
      sidebarContent={
        <>
          {ungroupedItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>{ungroupedItems.map(renderNavItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
          {NAV_GROUP_ORDER.map((group) => {
            const groupItems = items.filter((item) => item.group === group);
            if (groupItems.length === 0) return null;
            return (
              <SidebarGroup key={group}>
                <SidebarGroupLabel>{t(NAV_GROUP_LABEL_KEYS[group])}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>{groupItems.map(renderNavItem)}</SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
        </>
      }
      sidebarFooter={
        <>
          <SidebarSeparator className="mx-0" />
          <SelfShiftWidget venueId={venueId} />
          {canEnterAdmin && (
            <>
              <SidebarSeparator className="mx-0" />
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive('/admin')}
                    tooltip={t('nav.franchiseAdmin')}
                  >
                    <Link to="/admin">
                      <Building2 aria-hidden="true" />
                      <span>{t('nav.franchiseAdmin')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </>
          )}
          {user ? <SidebarAccountMenu name={user.name} email={user.email} /> : null}
        </>
      }
    />
  );
}

function isVisible(item: NavItem, auth: Authorizer, venueId: string): boolean {
  if (!item.permissions) return true;
  return auth.canAny(item.permissions, { kind: 'venue', venueId });
}
